import { useQuery } from '@tanstack/react-query';

import type { OrderDetails } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get a single order (purchased vehicle) details by ID
 * @param servicesProviderId - Required services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 * @param timezone - Optional IANA timezone string (defaults to browser timezone)
 */
export const getOrderDetails = async (
	servicesProviderId: string,
	orderId: string,
	timezone?: string,
): Promise<OrderDetails> => {
	const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const response = await apiClient.get<OrderDetails>(
		ordersEndpoints.details(servicesProviderId, orderId),
		{ params: { timezone: tz } },
	);
	return response.data;
};

/**
 * Hook for fetching a single order (purchased vehicle) details by ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetOrderDetails = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '').queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to fetch order without an order ID.');
			}
			return getOrderDetails(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
